// src/components/DashboardSidebar.js
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../apiClient";
import config from "../config";
import IVMRReportPage from "./IVMRReportPage";
import PayrollReportPage from "./PayrollReportPage";
import UserManagement from "./UserManagement";
import GenericLicenseManagement from "./GenericLicenseManagement";
import "./DashboardSidebarStyles.css";
import { BarChart3, User, Building2, FileSpreadsheet, Truck, LogOut, Mail, Phone } from "lucide-react";
import useIsMobile from "../hooks/useIsMobile";

function DashboardSidebar() {
  const [activeTab, setActiveTab] = useState("ivmr");
  const [userInfo, setUserInfo] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [companyMembers, setCompanyMembers] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showIVMRLicenseManagement, setShowIVMRLicenseManagement] = useState(false);
  const [showPayrollReportLicenseManagement, setShowPayrollReportLicenseManagement] = useState(false);
  
  // Motive API Key state
  const [motiveApiKey, setMotiveApiKey] = useState("");
  const [editingApiKey, setEditingApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [apiKeyError, setApiKeyError] = useState("");
  const [apiKeySuccess, setApiKeySuccess] = useState("");

  // User profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileInput, setProfileInput] = useState({ fullName: "", phoneNumber: "" });
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");

  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const adminCount = useMemo(() => 
    companyMembers.filter(m => m.role === "admin").length, 
    [companyMembers]
  );

  // Fetch Firestore data when user is available
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user || user.needsSignup) return;

      try {
        // Fetch user info from API
        const userResponse = await api.get("/api/me");
        const userData = userResponse.data;
        setUserInfo(userData);

        // Fetch company info from API
        const companyResponse = await api.get("/api/company");
        const companyData = companyResponse.data;
        setCompanyInfo(companyData);

        // Fetch company members to count admins
        const membersResponse = await api.get("/api/company/members");
        setCompanyMembers(membersResponse.data);

        // Fetch Motive API key if admin
        if (userData.role === "admin") {
          try {
            const apiKeyResponse = await api.get("/api/company/motive-api-key");
            setMotiveApiKey(apiKeyResponse.data.motiveApiKey || "");
          } catch (err) {
            console.error("Error fetching Motive API key:", err);
          }
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserData();
  }, [user]);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Error signing out:", err);
    }
  };

  const toggleSidebar = () => setSidebarCollapsed(!sidebarCollapsed);

  const isMobile = useIsMobile(768);

  // Support contact used for Contact Us. Prefer config values if provided.
  const supportEmail = config?.SUPPORT_EMAIL || "contact@fxfleet.report";
  const supportPhone = config?.SUPPORT_PHONE || "+1 (610) 749-7904";

  // Unified hamburger handler: on mobile toggle overlay, on desktop toggle collapse
  const handleHamburgerClick = () => {
    if (isMobile) {
      setIsSidebarOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  };

  // Close button inside the sidebar header (X). On mobile close overlay, on desktop collapse.
  const closeSidebarFromHeader = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    } else {
      setSidebarCollapsed(true);
    }
  };

  const maskApiKey = (key) => {
    if (!key) return "Not configured";
    if (key.length <= 8) return "••••••••";
    return key.substring(0, 4) + "••••••••" + key.substring(key.length - 4);
  };

  const updateMotiveApiKey = async () => {
    if (userInfo?.role !== "admin") {
      setApiKeyError("Only admins can update the Motive API key");
      return;
    }

    setApiKeyError("");
    setApiKeySuccess("");

    try {
      await api.put("/api/company/motive-api-key", { motiveApiKey: apiKeyInput });
      setMotiveApiKey(apiKeyInput);
      setEditingApiKey(false);
      setApiKeySuccess("Motive API key updated successfully!");
      
      // Update companyInfo state
      setCompanyInfo(prev => ({
        ...prev,
        motiveApiKey: apiKeyInput
      }));

      setTimeout(() => setApiKeySuccess(""), 3000);
    } catch (e) {
      setApiKeyError(e?.response?.data?.error || "Failed to update Motive API key");
    }
  };

  const updateUserProfile = async () => {
    setProfileError("");
    setProfileSuccess("");

    if (!profileInput.fullName.trim()) {
      setProfileError("Full name is required");
      return;
    }

    try {
      await api.put("/api/me", {
        fullName: profileInput.fullName,
        phoneNumber: profileInput.phoneNumber
      });

      setUserInfo(prev => ({
        ...prev,
        fullName: profileInput.fullName,
        phoneNumber: profileInput.phoneNumber
      }));

      setEditingProfile(false);
      setProfileSuccess("Profile updated successfully!");
      setTimeout(() => setProfileSuccess(""), 3000);
    } catch (e) {
      setProfileError(e?.response?.data?.error || "Failed to update profile");
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      {/* Sidebar */}
      {/* Mobile header (hamburger) — hide when the sidebar overlay is open so it
          doesn't overlap the sidebar header/logo on small screens */}
      {!isSidebarOpen && (
        <header className="mobile-header">
          <button className="menu-toggle" onClick={() => setIsSidebarOpen(!isSidebarOpen)}>☰</button>
          <h1 className="mobile-title">Dashboard</h1>
        </header>
      )}
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-section">
            <Truck className="logo-icon" size={28} />
            {!sidebarCollapsed && (
              <span className="logo-text font-semibold text-lg">
                FX Fleet Reports
              </span>
            )}
          </div>
          <div className="sidebar-controls">
            {/* Desktop hamburger: visible on wider screens via CSS. It toggles collapse on desktop
                and acts like the mobile hamburger on small screens if shown. */}
            <button
              className="hamburger-desktop"
              onClick={handleHamburgerClick}
              aria-label="Toggle sidebar"
              title="Toggle sidebar"
            >
              ☰
            </button>

            {/* Close / X button — shown when the sidebar is expanded (desktop) or when overlay is open (mobile)
                Clicking it collapses/closes the sidebar depending on viewport. */}
            <button
              className="sidebar-toggle"
              onClick={closeSidebarFromHeader}
              title="Close sidebar"
            >
              ×
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === "ivmr" ? "active" : ""}`}
            onClick={() => setActiveTab("ivmr")}
          >
            <BarChart3 className="nav-icon" size={20} />
            {!sidebarCollapsed && <span className="nav-text">IVMR</span>}
          </button>

          <button
            className={`nav-item ${activeTab === "payrollreport" ? "active" : ""}`}
            onClick={() => setActiveTab("payrollreport")}
          >
            <FileSpreadsheet className="nav-icon" size={20} />
            {!sidebarCollapsed && <span className="nav-text">Payroll Report</span>}
          </button>

          {/* Account moved to footer user-info click - removed from sidebar nav */}

          <button
            className={`nav-item ${activeTab === "company" ? "active" : ""}`}
            onClick={() => setActiveTab("company")}
          >
            <Building2 className="nav-icon" size={20} />
            {!sidebarCollapsed && <span className="nav-text">Company</span>}
          </button>
        </nav>

        <div className="sidebar-footer">
          {/* Contact Us - appears above user info */}
          <div className="contact-us" style={{ padding: '8px 20px' }}>
            {/* Stack email and phone vertically, each on its own row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
              <a
                href={`mailto:${supportEmail}?subject=${encodeURIComponent('FX Fleet Reports Support')}`}
                className="contact-link contact-item"
                title="Email support"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', width: '100%' }}
              >
                <Mail className="contact-icon" size={16} />
                {!sidebarCollapsed && <span className="contact-text">{supportEmail}</span>}
              </a>

              <a
                href={`tel:${supportPhone.replace(/[^+0-9]/g, '')}`}
                className="contact-phone contact-item"
                title="Call support"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', width: '100%' }}
              >
                <Phone className="contact-icon" size={16} />
                {!sidebarCollapsed && <span className="contact-text">{supportPhone}</span>}
              </a>
            </div>
          </div>

          <div
            className="user-info"
            role="button"
            tabIndex={0}
            title="Open Account"
            onClick={() => setActiveTab("account")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setActiveTab("account");
            }}
          >
            {!sidebarCollapsed && user && (
              <div className="user-details">
                <div className="user-name">{userInfo?.fullName || user.displayName || user.email}</div>
                <div className="user-role">{userInfo?.role || "User"}</div>
              </div>
            )}
          </div>
          <button className="sign-out-btn" onClick={handleSignOut} title="Sign Out">
            <LogOut className="nav-icon" size={20} />
            {!sidebarCollapsed && <span className="nav-text">Sign Out</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1 className="page-title">
            {activeTab === "ivmr" && "Individual Vehicle Mileage Report"}
            {activeTab === "payrollreport" && "Payroll Report"}
            {activeTab === "account" && "Account Information"}
            {activeTab === "company" && "Company Settings"}
          </h1>
        </div>

        <div className="content-body">
          {activeTab === "ivmr" && <IVMRReportPage />}
          {activeTab === "payrollreport" && <PayrollReportPage />}

          {activeTab === "account" && (
            <div className="account-section">
              <div className="info-card">
                {profileSuccess && (
                  <div className="alert alert-success" style={{ marginBottom: '20px' }}>
                    {profileSuccess}
                  </div>
                )}
                {profileError && (
                  <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    {profileError}
                  </div>
                )}

                {/* Profile Editing Section */}
                {!editingProfile ? (
                  <>
                    <div className="info-row">
                      <label>Full Name:</label>
                      <span>{userInfo?.fullName || "Not provided"}</span>
                    </div>
                    <div className="info-row">
                      <label>Phone Number:</label>
                      <span>{userInfo?.phoneNumber || "Not provided"}</span>
                    </div>
                    <div className="info-row">
                      <label>Email:</label>
                      <span>{user?.email}</span>
                    </div>
                    <div className="info-row">
                      <label></label>
                      <button 
                        className="btn-small btn-primary"
                        onClick={() => {
                          setEditingProfile(true);
                          setProfileInput({
                            fullName: userInfo?.fullName || "",
                            phoneNumber: userInfo?.phoneNumber || ""
                          });
                          setProfileError("");
                          setProfileSuccess("");
                        }}
                      >
                        Edit Profile
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="info-row">
                      <label>Full Name: *</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Enter full name"
                        value={profileInput.fullName}
                        onChange={(e) => setProfileInput(prev => ({ ...prev, fullName: e.target.value }))}
                        style={{ flex: 1 }}
                      />
                    </div>
                    <div className="info-row">
                      <label>Phone Number:</label>
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="Enter phone number"
                        value={profileInput.phoneNumber}
                        onChange={(e) => setProfileInput(prev => ({ ...prev, phoneNumber: e.target.value }))}
                        style={{ flex: 1 }}
                      />
                    </div>
                    <div className="info-row">
                      <label>Email:</label>
                      <span>{user?.email}</span>
                    </div>
                    <div className="info-row">
                      <label></label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                          className="btn-small btn-primary"
                          onClick={updateUserProfile}
                        >
                          Save Changes
                        </button>
                        <button 
                          className="btn-small btn-secondary"
                          onClick={() => {
                            setEditingProfile(false);
                            setProfileInput({ fullName: "", phoneNumber: "" });
                            setProfileError("");
                            setProfileSuccess("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </>
                )}

                <div className="info-row">
                  <label>Role:</label>
                  <span className="role-badge">{userInfo?.role || "User"}</span>
                </div>
                <div className="info-row">
                  <label>Email Verified:</label>
                  <span className={`status-badge ${user?.emailVerified ? "verified" : "unverified"}`}>
                    {user?.emailVerified ? "✓ Verified" : "⚠ Unverified"}
                  </span>
                </div>
                <div className="info-row">
                  <label>Account Created:</label>
                  <span>{user?.metadata?.creationTime ? new Date(user.metadata.creationTime).toLocaleDateString() : "Unknown"}</span>
                </div>
                <div className="info-row">
                  <label>Last Sign In:</label>
                  <span>{user?.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleDateString() : "Unknown"}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === "company" && (
            <div className="company-section">
              <div className="info-card">
                <div className="info-row">
                  <label>Company Name:</label>
                  <span>{companyInfo?.name || userInfo?.company || "Not provided"}</span>
                </div>
                <div className="info-row">
                  <label>Total Users:</label>
                  <span>
                    {companyMembers.length} total ({adminCount} {adminCount === 1 ? 'admin' : 'admins'}, {companyMembers.length - adminCount} {companyMembers.length - adminCount === 1 ? 'member' : 'members'})
                  </span>
                </div>
                
                {/* Motive API Key Section */}
                <div className="info-row">
                  <label>Motive API Key:</label>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flex: 1 }}>
                    {!editingApiKey ? (
                      <>
                        <span className="api-key font-mono">
                          {maskApiKey(motiveApiKey)}
                        </span>
                        {userInfo?.role === "admin" && (
                          <button 
                            className="btn-small btn-primary"
                            onClick={() => {
                              setEditingApiKey(true);
                              setApiKeyInput(motiveApiKey);
                              setApiKeyError("");
                              setApiKeySuccess("");
                            }}
                          >
                            {motiveApiKey ? "Update" : "Set"} API Key
                          </button>
                        )}
                      </>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, alignItems: 'flex-end' }}>
                        {apiKeyError && <div className="alert alert-error" style={{ margin: 0, width: '100%' }}>{apiKeyError}</div>}
                        {apiKeySuccess && <div className="alert alert-success" style={{ margin: 0, width: '100%' }}>{apiKeySuccess}</div>}
                        
                        <input
                          type="text"
                          className="api-key-input"
                          placeholder="Enter Motive API key"
                          value={apiKeyInput}
                          onChange={(e) => setApiKeyInput(e.target.value)}
                          disabled={userInfo?.role !== "admin"}
                          style={{ width: '100%' }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button 
                            className="btn-small btn-primary"
                            onClick={updateMotiveApiKey}
                            disabled={userInfo?.role !== "admin"}
                          >
                            Save
                          </button>
                          <button 
                            className="btn-small btn-secondary"
                            onClick={() => {
                              setEditingApiKey(false);
                              setApiKeyInput("");
                              setApiKeyError("");
                              setApiKeySuccess("");
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                        {userInfo?.role !== "admin" && (
                          <small className="text-muted" style={{ margin: 0 }}>
                            Only admins can update the Motive API key
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {userInfo?.role === "admin" && (
                <div className="admin-actions">
                  <h3>Admin Actions</h3>
                  <button 
                    className="action-btn"
                    onClick={() => setShowUserManagement(true)}
                  >
                    Manage Users
                  </button>
                  <button 
                    className="action-btn"
                    onClick={() => setShowIVMRLicenseManagement(true)}
                  >
                    Manage IVMR Licenses
                  </button>
                  <button 
                    className="action-btn"
                    onClick={() => setShowPayrollReportLicenseManagement(true)}
                  >
                    Manage Payroll Report Licenses
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Management Modal */}
      {showUserManagement && (
        <UserManagement 
          onClose={() => setShowUserManagement(false)}
          userInfo={userInfo}
        />
      )}

      {/* IVMR License Modal */}
      {showIVMRLicenseManagement && (
        <GenericLicenseManagement 
          licenseType="ivmr"
          displayName="IVMR"
          onClose={() => setShowIVMRLicenseManagement(false)}
          userInfo={userInfo}
        />
      )}

      {/* Payroll Report License Modal */}
      {showPayrollReportLicenseManagement && (
        <GenericLicenseManagement 
          licenseType="payrollreport"
          displayName="Payroll Report"
          onClose={() => setShowPayrollReportLicenseManagement(false)}
          userInfo={userInfo}
        />
      )}
      
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
      )}
    </div>
  );
}

export default DashboardSidebar;